import type { ComponentType, SVGProps } from 'react';
import type { Role } from '@servwell/shared';
import {
  IconAlerts,
  IconToday,
  IconSchedule,
  IconFleet,
  IconPipeline,
  IconRequests,
  IconQuotes,
  IconJobs,
  IconTickets,
  IconProjects,
  IconMoney,
  IconCustomers,
  IconProducts,
  IconInventory,
  IconReports,
} from './icons';

/**
 * The locked IA — "Two Zones" (PRD v2 §4). WORK ×7 · BUSINESS ×4 · Settings in the
 * footer. This model is the single source of truth for the office sidebar; pages and
 * the route tree derive from it, never restate it.
 */
export type { Role };

export type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;
  /**
   * Optional count pill. `alerts` is the one drawn (ops insights Phase 7: `alert.count`, on the
   * sidebar item and the phone's More button, `alerts/AlertCount.tsx`); visits today, unpaid
   * invoices and low stock are declared and not wired yet.
   */
  pill?: 'alerts' | 'visits' | 'unpaid' | 'priceRaises';
  /** Roles that see this item. Absent = all office-shell roles. */
  roles?: readonly Role[];
};

export type NavZone = { label: string; items: NavItem[] };

/**
 * The roles that work the office surface.
 *
 * A LITERAL, deliberately. It was briefly derived from `can(r, 'org', 'read')` — which
 * matches today and is the wrong reason: `org:read` is "may see organisation settings", and
 * this list also decides who sees Schedule, Jobs and Inventory. Grant a fifth role basic
 * organisation visibility and the derivation would silently hand it the whole operational
 * nav. A roster of who works this surface is its own product fact, not a restatement of a
 * permission — so it is written down, and `forbidden-state.test.ts` fails loudly if it and
 * the permission matrix ever stop agreeing, leaving a human to decide which one moved.
 *
 * EXPORTED because the ROUTE TREE gates on it too. A sidebar that hides a link while the
 * router leaves the door open is a decoration, not a permission — which is what `/settings`
 * was for `office` and `bookkeeper` alike.
 */
export const OFFICE_ONLY: readonly Role[] = ['owner', 'office'];

/**
 * Who may open the job pages, the Jobs list and a job's own page: the office's roster, named
 * so that a screen linking to a job (the Revenue page's drill) links only for the roles the
 * router lets in, and a change to who may open a job changes both. The routes gate on this
 * name; `forbidden-state.test.ts` reads it as `OFFICE_ONLY`, which it is.
 */
export const JOB_PAGES_ROLES: readonly Role[] = OFFICE_ONLY;

/**
 * Who may enter the FIELD shell at all.
 *
 * A tech, obviously — and the office, because dispatch opens a tech's stop to see what was
 * photographed and where the job actually got to. `visit.get` already authorises exactly
 * those three, and row-scopes the tech to their own assignments.
 *
 * ⚠️ A `bookkeeper` is the one this excludes, and until now `/tech` was mounted for every
 * authenticated role: they got the field shell followed by "You don't have access to the
 * field schedule", and `/tech/van` rendered an inventory surface with a Restock button whose
 * mutation would 403. Redirect-safe because `RoleHome` sends a bookkeeper to `/money`.
 */
export const FIELD_ROLES: readonly Role[] = ['owner', 'office', 'tech'];

export const NAV_ZONES: NavZone[] = [
  {
    label: 'Work',
    items: [
      {
        to: '/today',
        label: 'Today',
        icon: IconToday,
        pill: 'visits',
        roles: OFFICE_ONLY,
      },
      /**
       * What the monitors found that needs someone (ops insights Phase 7, spec §6), right after
       * Today. The owner and the office: the roles that hold `alert:read`. On a phone it falls
       * under More, so its count is drawn on the More button too.
       */
      {
        to: '/alerts',
        label: 'Alerts',
        icon: IconAlerts,
        pill: 'alerts',
        roles: OFFICE_ONLY,
      },
      { to: '/schedule', label: 'Schedule', icon: IconSchedule, roles: OFFICE_ONLY },
      /**
       * The fleet page (112). ⭐ D5 IS DONE: this entry replaces Map, which is why it sits
       * where Map sat — next to Schedule, because it is the same day drawn differently.
       *
       * Everything `/map` did lives on the Routes tab: the day pager, the stops in order,
       * the calls that cannot be drawn WITH their reasons, the unassigned group, and the
       * "look them up now" control — that last one now gated on `org:manage`, which
       * `/map` rendered ungated so an office dispatcher's click failed with
       * `Missing permission org:manage`.
       *
       * The REGISTER stays at `/fleet/assets`, reached from this page's header and both
       * "View all" links. 107 aimed this entry there because it was the only fleet
       * surface that existed, and said so here.
       */
      { to: '/fleet', label: 'Fleet', icon: IconFleet, roles: OFFICE_ONLY },
      { to: '/pipeline', label: 'Pipeline', icon: IconPipeline, roles: OFFICE_ONLY },
      { to: '/requests', label: 'Requests', icon: IconRequests, roles: OFFICE_ONLY },
      { to: '/quotes', label: 'Quotes', icon: IconQuotes, roles: OFFICE_ONLY },
      { to: '/jobs', label: 'Jobs', icon: IconJobs, roles: OFFICE_ONLY },
      // Problems after a visit (ops insights Phase 5a): the office works them here.
      { to: '/tickets', label: 'Tickets', icon: IconTickets, roles: OFFICE_ONLY },
      // No `roles`: a bookkeeper holds `project:read` because a project IS a P&L view, and
      // hiding the link while the server served the page was the drift ticket 028 is about.
      // The write controls on the page check `project:manage`.
      { to: '/projects', label: 'Projects', icon: IconProjects },
    ],
  },
  {
    label: 'Business',
    items: [
      { to: '/money', label: 'Money', icon: IconMoney, pill: 'unpaid' },
      { to: '/customers', label: 'Customers', icon: IconCustomers },
      /**
       * The price book's own page (CRM-18), beside Inventory. OFFICE_ONLY because it is every
       * sell price the shop charges: `priceBook.*` refuses a tech and the bookkeeper, and the
       * approved CRM nav leaves it out of the bookkeeper's set. On a phone it falls under More.
       */
      {
        to: '/products',
        label: 'Products & services',
        icon: IconProducts,
        roles: OFFICE_ONLY,
      },
      {
        to: '/inventory',
        label: 'Inventory',
        icon: IconInventory,
        pill: 'priceRaises',
        roles: OFFICE_ONLY,
      },
      { to: '/reports', label: 'Reports', icon: IconReports },
    ],
  },
];

export function zonesForRole(role: Role): NavZone[] {
  return NAV_ZONES.map((z) => ({
    ...z,
    items: z.items.filter((i) => !i.roles || i.roles.includes(role)),
  })).filter((z) => z.items.length > 0);
}

/**
 * The three destinations the phone bar promotes — **Today · Schedule · Jobs** (A2, approved
 * 2026-08-22). Everything else goes under More.
 *
 * ⚠️ A LITERAL, and NOT "the first three of `NAV_ZONES`", which would silently yield
 * Today · Schedule · Fleet. Which three surfaces earn a permanent thumb position is a
 * product judgement about a 390px screen; the sidebar's order is a reading order for a
 * list of twelve. Deriving one from the other would make a re-ordering of the sidebar
 * quietly re-plumb the phone bar.
 *
 * It is a list of `to` values rather than items so it cannot drift into a second copy of
 * the labels or icons — those still come from `NAV_ZONES`, once.
 */
export const MOBILE_TAB_PRIORITY: readonly string[] = ['/today', '/schedule', '/jobs'];

/** How many nav slots the bar has beside More. Four total keeps the measured 52px tap
 *  targets; a fifth truncates "Schedule" (115, measured). */
export const MOBILE_TAB_SLOTS = 3;

/**
 * The phone bar's split: which items get a slot, and which fall to More.
 *
 * ⭐ ONE SOURCE OF TRUTH, TWO CHROMES. This reads `zonesForRole` — the same function the
 * sidebar reads — so an item hidden from a role in one chrome cannot appear in the other.
 * The lockstep test asserts exactly that: `tabs ∪ more` is the role's visible set, with
 * nothing added, nothing dropped and nothing counted twice.
 *
 * ⚠️ The backfill is not cosmetic. A `bookkeeper` sees none of the three priorities
 * (`/today`, `/schedule` and `/jobs` are all `OFFICE_ONLY`), so a bar built from the
 * priority list alone would hand them a single More button — a chrome that hides the four
 * surfaces they actually work. Backfilling in nav order gives them Projects · Money ·
 * Customers, which is their own reading order, not a leftover of somebody else's.
 */
export function mobileTabsForRole(role: Role): { tabs: NavItem[]; more: NavItem[] } {
  const visible = zonesForRole(role).flatMap((z) => z.items);
  const tabs: NavItem[] = [];
  for (const to of MOBILE_TAB_PRIORITY) {
    const item = visible.find((i) => i.to === to);
    if (item) tabs.push(item);
  }
  for (const item of visible) {
    if (tabs.length >= MOBILE_TAB_SLOTS) break;
    if (!tabs.includes(item)) tabs.push(item);
  }
  /* ⚠️ `more` is filtered against the SLICED list, not the pre-slice one. Filtering
     against `tabs` would make a priority list longer than the slot count drop its overflow
     from BOTH chromes — an item that exists, is permitted, and is reachable from nowhere.
     The partition has to be a partition. */
  const picked = tabs.slice(0, MOBILE_TAB_SLOTS);
  return { tabs: picked, more: visible.filter((i) => !picked.includes(i)) };
}

/**
 * May this role open a surface gated on `roles`?
 *
 * Pure, and exported, because the route tree's version of this question could otherwise only
 * be checked by reading source text — and the whole point of Wave 0.1 is that a door is not
 * proven by the shape of the code that declares it. An absent list means "no extra gate":
 * the office shell has already turned techs away by the time this is asked.
 */
export function mayEnter(role: Role, roles?: readonly Role[]): boolean {
  return !roles || roles.includes(role);
}
